/**
 * Home.tsx
 * =============================================================================
 * Main landing page for Neuro-Audio Lab.
 * Shows welcome screen before audio initialization, then displays the player.
 * 
 * REBRANDED v3.0: Layout compacto, paleta Azul/Violeta/Teal, sin rojo.
 * 
 * @author Neuro-Audio Lab
 * @version 3.0.0 - Rebranding
 */

import { ErrorBanner } from '../components/ui/ErrorBanner';
import { APP_NAME, APP_VERSION, APP_STUDIO, APP_COUNTRY } from '../config/constants';
import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Headphones, Sparkles, Brain, Moon, Heart, FlaskConical } from 'lucide-react';
import { Player } from '../components/player/Player';
import { useAudioEngine } from '../hooks/useAudioEngine';
import type { AudioMode } from '../core/types';

// =============================================================================
// MODE CARD COMPONENT
// =============================================================================

interface ModeCardProps {
  mode: {
    id: AudioMode;
    name: string;
    description: string;
    icon: string;
    color: string;
  };
  onClick: (mode: AudioMode) => void;
  isLoading: boolean;
}

function ModeCard({ mode, onClick, isLoading }: ModeCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  
  // Icon component mapping
  const IconComponent = mode.id === 'focus' ? Brain : mode.id === 'sleep' ? Moon : Heart;
  
  return (
    <button
      onClick={() => onClick(mode.id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={isLoading}
      className={`
        group relative p-6 rounded-2xl
        backdrop-blur-md border border-white/10
        transition-all duration-300 ease-out
        ${isLoading ? 'opacity-50 cursor-wait' : 'hover:scale-105 hover:border-white/20 active:scale-95'}
        ${isHovered ? 'shadow-2xl' : 'shadow-lg'}
      `}
      style={{
        background: isHovered
          ? `linear-gradient(135deg, ${mode.color}20, ${mode.color}05)`
          : 'rgba(255, 255, 255, 0.03)',
        boxShadow: isHovered
          ? `0 20px 60px ${mode.color}40`
          : '0 10px 40px rgba(0, 0, 0, 0.3)',
      }}
    >
      {/* Icon */}
      <div className="flex justify-center mb-4">
        <div
          className="p-4 rounded-xl transition-colors duration-300"
          style={{
            background: isHovered
              ? `${mode.color}20`
              : 'rgba(255, 255, 255, 0.05)',
          }}
        >
          <IconComponent
            className="w-8 h-8 transition-colors duration-300"
            style={{ color: isHovered ? mode.color : '#ffffff80' }}
          />
        </div>
      </div>
      
      {/* Text Content */}
      <div className="text-center space-y-2">
        <h3 className="text-xl font-medium text-neutral-100">
          {mode.name}
        </h3>
        <p className="text-sm text-neutral-400 leading-relaxed">
          {mode.description}
        </p>
      </div>
      
      {/* Hover Indicator - PADDING AUMENTADO */}
      <div
        className={`
          absolute bottom-3 left-1/2 -translate-x-1/2
          text-xs font-medium transition-all duration-300
          pt-3
          ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
        `}
        style={{ color: mode.color }}
      >
        Comenzar →
      </div>
    </button>
  );
}

// =============================================================================
// WELCOME SCREEN COMPONENT
// =============================================================================

interface WelcomeScreenProps {
  onStart: (mode?: AudioMode) => void;
  isLoading: boolean;
}

function WelcomeScreen({ onStart, isLoading }: WelcomeScreenProps) {
  // Mode configurations
  const modes = [
    {
      id: 'focus' as AudioMode,
      name: 'Enfoque Profundo',
      description: 'Ruido marrón esculpido + modulación isocrónica Beta para trabajo profundo.',
      icon: '🎯',
      color: '#3B82F6', // Blue
    },
    {
      id: 'sleep' as AudioMode,
      name: 'Sueño Reparador',
      description: 'Paisajes sonoros documentales (Ecuador) + arrastre Delta para inducir fase NREM.',
      icon: '🌙',
      color: '#8B5CF6', // Purple
    },
    {
      id: 'anxiety' as AudioMode,
      name: 'Reset de Ansiedad',
      description: 'Estimulación bilateral + guía respiratoria clínica para desactivación de la amígdala.',
      icon: '🧘',
      color: '#10B981', // Green
    },
  ];
  
  // Animated background particles
  const particles = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    size: Math.random() * 2.5 + 1,
    x: Math.random() * 100,
    y: Math.random() * 100,
    duration: Math.random() * 10 + 15,
    delay: Math.random() * 3,
  }));

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden px-6 py-8">
      {/* Animated Background - COLORES FRÍOS (AZUL/VIOLETA) */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Gradient Orbs - SIN ROJO */}
        <div 
          className="absolute w-80 h-80 rounded-full blur-3xl opacity-15 animate-pulse"
          style={{
            background: 'radial-gradient(circle, #3B82F6 0%, transparent 70%)',
            top: '5%',
            left: '10%',
            animationDuration: '8s',
          }}
        />
        <div 
          className="absolute w-96 h-96 rounded-full blur-3xl opacity-12 animate-pulse"
          style={{
            background: 'radial-gradient(circle, #8B5CF6 0%, transparent 70%)',
            bottom: '10%',
            right: '5%',
            animationDuration: '10s',
            animationDelay: '2s',
          }}
        />
        
        {/* Floating Particles */}
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full bg-white/15"
            style={{
              width: particle.size,
              height: particle.size,
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              animation: `float ${particle.duration}s ease-in-out infinite`,
              animationDelay: `${particle.delay}s`,
            }}
          />
        ))}
      </div>
      
      {/* Main Content - ESPACIADO REDUCIDO (space-y-8 en lugar de 12) */}
      <div className="relative z-10 w-full max-w-6xl mx-auto space-y-8">
        
        {/* Hero Section - MÁS COMPACTO */}
        <div className="text-center space-y-4">
          {/* Logo Icon - COLORES FRÍOS */}
          <div className="flex justify-center">
            <div 
              className="relative p-4 rounded-3xl"
              style={{
                background: 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(139,92,246,0.1))',
                boxShadow: '0 0 40px rgba(59,130,246,0.2)',
              }}
            >
              <Sparkles className="w-10 h-10 text-neutral-100" />
              
              {/* Pulse Ring - AZUL/VIOLETA */}
              <div 
                className="absolute inset-0 rounded-3xl animate-ping opacity-15"
                style={{ 
                  border: '2px solid #3B82F6',
                  animationDuration: '2.5s',
                }}
              />
            </div>
          </div>
          
          {/* Title & Subtitle */}
          <div>
            <h1 className="text-4xl md:text-5xl font-extralight tracking-tight text-neutral-200 mb-2">
              Neuro-Audio Lab
            </h1>
            <p className="text-base md:text-lg text-neutral-400 font-light">
              Arquitectura Cognitiva Basada en Psicoacústica
            </p>
          </div>
          
          {/* Headphones Reminder - MÁS COMPACTO */}
          <div className="flex items-center justify-center gap-2 text-neutral-400 text-xs">
            <Headphones className="w-4 h-4" />
            <span>Usa auriculares para la inmersión espacial y el arrastre neuronal</span>
          </div>
        </div>
        
        {/* Mode Selection Grid */}
        <div>
          <h2 className="text-center text-lg text-neutral-300 font-light mb-5">
            Selecciona tu estado mental objetivo
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {modes.map((mode) => (
              <ModeCard
                key={mode.id}
                mode={mode}
                onClick={onStart}
                isLoading={isLoading}
              />
            ))}
          </div>
        </div>

        {/* Science Link Button */}
        <div className="flex justify-center">
          <Link
            to="/science"
            className="group inline-flex items-center gap-2 px-5 py-3 rounded-xl
                       border border-white/10 bg-white/[0.02]
                       hover:border-white/20 hover:bg-white/[0.05]
                       transition-all duration-300"
          >
            <FlaskConical className="w-4 h-4 text-blue-400 group-hover:text-blue-300 transition-colors" />
            <span className="text-sm text-neutral-400 group-hover:text-neutral-100 transition-colors">
              Cómo Funciona
            </span>
          </Link>
        </div>

      </div>
      
      {/* CSS for float animation */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
            opacity: 0.15;
          }
          50% {
            transform: translateY(-15px) translateX(8px);
            opacity: 0.3;
          }
        }
      `}</style>
    </div>
  );
}

// =============================================================================
// MAIN HOME PAGE
// =============================================================================

export function Home() {
  const { isInitialized, isLoading, initialize, error, clearError } = useAudioEngine();
  const [showPlayer, setShowPlayer] = useState(false);
  const [selectedMode, setSelectedMode] = useState<AudioMode | undefined>();
  
  // Handle start with optional mode
  const handleStart = useCallback(async (mode?: AudioMode) => {
    try {
      setSelectedMode(mode);
      await initialize();
      setShowPlayer(true);
    } catch (err) {
      console.error('Failed to initialize:', err);
    }
  }, [initialize]);
  
  // Show player if already initialized (e.g., from previous session)
  useEffect(() => {
    if (isInitialized) {
      setShowPlayer(true);
    }
  }, [isInitialized]);

  // Show welcome screen or player
  if (!showPlayer) {
    return (
      <WelcomeScreen 
        onStart={handleStart} 
        isLoading={isLoading} 
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#141414] py-12">
      {error && (
      <ErrorBanner 
        message={error} 
        onDismiss={clearError} 
        autoDismiss={true} // Se cierra solo a los 5s
      />
    )}
      {/* Subtle Background Pattern - COLORES FRÍOS */}
      <div 
        className="fixed inset-0 pointer-events-none opacity-20"
        style={{
          backgroundImage: `radial-gradient(circle at 50% 50%, rgba(59,130,246,0.03) 0%, transparent 50%)`,
        }}
      />
      
      {/* Main Content */}
      <main className="relative z-10">
        <Player initialMode={selectedMode} />
      </main>
      
      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 py-4 text-center">
        <p className="text-xs text-neutral-400">
          {`${APP_NAME} ${APP_VERSION} · ${APP_STUDIO} · ${APP_COUNTRY} 🇪🇨`}
        </p>
      </footer>
    </div>
  );
}

export default Home;